import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import type { BulkInvitationActionRequest, BulkInvitationActionResult } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import i18next from 'i18next';

const cancelInvitations = async (
  input: BulkInvitationActionRequest
): Promise<BulkInvitationActionResult> => {
  const response = await api.post('/dashboard/affiliates/invitations/cancel', input);

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to cancel invitations');
  }

  return response.data.data as BulkInvitationActionResult;
};

/**
 * Cancel (remove) one or many invitations through the unified endpoint. The
 * per-row button passes `{ ids: [id] }`; the bulk bar passes the full selection
 * or a `{ filter }`. The response summary drives the toast.
 */
export const useCancelInvitationsAction = () => {
  const queryClient = useQueryClient();

  return useMutation<
    BulkInvitationActionResult,
    AxiosError<{ message?: string }>,
    BulkInvitationActionRequest
  >({
    mutationFn: cancelInvitations,
    onSuccess: ({ processed, skipped }) => {
      queryClient.invalidateQueries({ queryKey: ['program-invitations'] });

      toaster.success({
        title: i18next.t('invitations.bulkCancel.successTitle'),
        description:
          skipped.length === 0
            ? i18next.t('invitations.bulkCancel.successAll', { count: processed })
            : i18next.t('invitations.bulkCancel.successPartial', {
                processed,
                skipped: skipped.length,
              }),
      });
    },
    onError: (error) => {
      console.error('Failed to cancel invitations:', error);
      toaster.error({
        title: i18next.t('invitations.bulkCancel.errorTitle'),
        description: getApiErrorMessage(error, i18next.t),
      });
    },
  });
};
